/**
 * Post-Schedule Block Classifier — read-only derivation.
 * ─────────────────────────────────────────────────────────────────
 * A job that is scheduled for today should have cleared the
 * Material-Gated Install Scheduling gate AND the permit gate at the
 * moment of scheduling. So when Install Today renders a row as
 * "Blocked", the operator/QA reviewer needs to know WHY it appears
 * blocked after it was supposedly cleared.
 *
 * This pure helper inspects the inputs that already flow into the
 * Install Today page and projects ONE deterministic classification:
 *
 *   - "late_breaking": at least one blocking signal was created
 *     AFTER `scheduledInstallDate` (post-schedule vendor cancel,
 *     field feedback, substitution filed, issue or ticket opened).
 *     This is the only legitimate post-schedule block in a properly
 *     run pipeline.
 *
 *   - "pre_schedule_drift": the blocking signals all pre-date the
 *     scheduled install date — meaning the scheduling gate either
 *     wasn't enforced or the canonical state has drifted. In the
 *     prototype this is almost always seed inconsistency; in live
 *     operation it is a hard QA escape worth investigating.
 *
 *   - "none": no blockers (caller should not have asked).
 *
 * Pure. No mutations. No new truth. No I/O.
 */
import type { Issue, Job } from "@/data/types";
import type { ServiceTicket } from "@/data/serviceTicketTypes";
import type {
  FieldMaterialFeedbackEvent,
  SubstitutionRequest,
} from "@/data/VendorPOContext";

export type PostScheduleBlockCause = "late_breaking" | "pre_schedule_drift" | "none";

export interface PostScheduleBlockVerdict {
  cause: PostScheduleBlockCause;
  /** Short operator label. Stable copy — safe to render directly. */
  label: string;
  /** One-line explanation for tooltip / inline help. */
  detail: string;
}

const NONE: PostScheduleBlockVerdict = {
  cause: "none",
  label: "",
  detail: "",
};

function toMs(iso: string | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

export interface PostScheduleBlockInput {
  job: Job;
  /** True when the row has at least one active blocker. */
  hasBlocker: boolean;
  activeIssues: Issue[];
  activeTickets: ServiceTicket[];
  unresolvedFieldFeedback: FieldMaterialFeedbackEvent[];
  openSubstitutionRequests: SubstitutionRequest[];
}

export function classifyPostScheduleBlock(
  input: PostScheduleBlockInput,
): PostScheduleBlockVerdict {
  if (!input.hasBlocker) return NONE;

  const scheduledMs = toMs(input.job.scheduledInstallDate);
  // If we don't have a scheduled date there's nothing to compare
  // against; treat any blocker as pre-schedule (data drift) since
  // the row should not be on the board today in the first place.
  if (scheduledMs === null) {
    return {
      cause: "pre_schedule_drift",
      label: "Pre-schedule (data drift)",
      detail:
        "This job has blockers but no scheduled install date — review canonical state; the scheduling gate would normally prevent this.",
    };
  }

  // A signal is "late-breaking" when its creation timestamp is
  // strictly AFTER the scheduled install date. Same-day signals
  // count as late-breaking (the gate cleared earlier in the day).
  const isAfterSchedule = (iso: string | undefined): boolean => {
    const t = toMs(iso);
    return t !== null && t >= scheduledMs;
  };

  const lateIssue = input.activeIssues.some((i) => isAfterSchedule(i.dateCreated));
  const lateTicket = input.activeTickets.some((t) => isAfterSchedule(t.createdAt));
  const lateFeedback = input.unresolvedFieldFeedback.some((f) =>
    isAfterSchedule(f.createdAt),
  );
  const lateSub = input.openSubstitutionRequests.some((s) =>
    isAfterSchedule(s.createdAt),
  );

  if (lateIssue || lateTicket || lateFeedback || lateSub) {
    const reasons: string[] = [];
    if (lateIssue) reasons.push("issue opened");
    if (lateTicket) reasons.push("service ticket opened");
    if (lateFeedback) reasons.push("field material feedback");
    if (lateSub) reasons.push("substitution filed");
    return {
      cause: "late_breaking",
      label: "Late-breaking",
      detail: `Blocker appeared after scheduling (${reasons.join(", ")}). This is the expected path for post-schedule disruptions.`,
    };
  }

  return {
    cause: "pre_schedule_drift",
    label: "Pre-schedule (data drift)",
    detail:
      "All blocking signals pre-date the scheduled install date — the scheduling gate would normally prevent this. In the prototype this typically indicates seed inconsistency; in live operation it is a QA escape worth investigating.",
  };
}
